import pluralize from 'pluralize';
import { Button, Dialog, DialogContent, Stack, Typography, useTheme } from '@mui/material';

import { type ConnectStatementsInstitution } from '../hooks/useConnectStatementsData';

import BankListCard from './BankListCard';
import SyncInstitutionRow from './SyncInstitutionRow';

import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import DialogCloseButton from 'modules/common/components/MUI/Dialog/components';

// The icon's disc fills 20 of its 25-unit box, so 50px draws Figma's 40px badge.
const SUCCESS_BADGE_SIZE = 50;

interface SyncProgressDialogProps {
    readonly open: boolean;
    /** The bank that just finished syncing. */
    readonly syncedInstitution: ConnectStatementsInstitution | undefined;
    /** Every bank Auto Statements can serve, synced or not, in list order. */
    readonly institutions: ConnectStatementsInstitution[];
    readonly enabledInstitutionIds: Set<string>;
    readonly onContinue: () => void;
    readonly onClose: () => void;
}

/**
 * Shown after a bank syncs while others are still waiting: what is done, what is left, and one
 * Continue that opens the connector on the next bank.
 */
export default function SyncProgressDialog({
    open,
    syncedInstitution,
    institutions,
    enabledInstitutionIds,
    onContinue,
    onClose,
}: Readonly<SyncProgressDialogProps>) {
    const theme = useTheme();
    const remainingCount = institutions.filter((inst) => !enabledInstitutionIds.has(inst.institutionId)).length;

    return (
        <Dialog open={open} onClose={onClose} size="medium" aria-labelledby="sync-progress-title">
            <DialogCloseButton onClick={onClose} aria-label="Close" sx={{ top: 30, right: 30 }} />
            {/* The theme pins every DialogContent's top padding at 24px with !important; Figma clears the
                close button with 64. */}
            <DialogContent sx={{ p: 4, paddingTop: ({ spacing }) => `${spacing(8)} !important` }}>
                <Stack alignItems="center" spacing={3}>
                    <Stack alignItems="center" spacing={2}>
                        <CheckCircleFilledIcon
                            fill={theme.palette.success.main}
                            sx={{ fontSize: SUCCESS_BADGE_SIZE }}
                        />
                        <Stack alignItems="center">
                            <Typography id="sync-progress-title" variant="labelLarge" component="h2" textAlign="center">
                                {syncedInstitution?.institutionName ?? 'Your bank'} is all set!
                            </Typography>
                            <Typography variant="body1" textAlign="center">
                                You have {remainingCount === 1 ? 'one' : remainingCount} more{' '}
                                {pluralize('bank', remainingCount)} to set up
                            </Typography>
                        </Stack>
                    </Stack>
                    <BankListCard>
                        {institutions.map((institution) => (
                            <SyncInstitutionRow
                                key={institution.institutionId}
                                institution={institution}
                                status={
                                    enabledInstitutionIds.has(institution.institutionId) ? 'enabled' : 'ready_to_sync'
                                }
                                expandable={false}
                            />
                        ))}
                    </BankListCard>
                    <Button variant="contained" size="large" fullWidth onClick={onContinue}>
                        Continue
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
